import { locales } from "senf-atomic-design-system/src/components/molecules/languageSettings/LanguageSettings";
import { getCloudFunctionUrl } from "../auth/firebase";
import { resolveLangForGeminiHttp } from "./geminiLangLabels";
import { parseGeminiHttpTextResponse } from "./geminiResponseParse";

export {
  getGeminiLangLabelForPrompt,
  GEMINI_LANG_LABEL_ENGLISH,
} from "./geminiLangLabels";

type GeminiRequestProps = {
  body: string;
  assistantId: string;
  lang?: keyof typeof locales | string;
  image?: {
    data: string;
    mimeType: string;
  };
  images?: Array<{
    data: string;
    mimeType: string;
  }>;
  signal?: AbortSignal;
  skipDlp?: boolean;
};

export const geminiRequest = async ({
  body,
  assistantId,
  lang,
  image,
  images,
  signal,
  skipDlp,
}: GeminiRequestProps) => {
  try {
    // Convert single image to images array if provided
    const finalImages = images || (image ? [image] : undefined);
    const functionUrl = getCloudFunctionUrl("gemini");

    const response = await fetch(functionUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        body: body,
        assistantId: assistantId,
        lang: resolveLangForGeminiHttp(lang),
        images: finalImages,
        ...(skipDlp ? { skipDlp } : {}),
      }),
      signal,
    });

    if (!response.ok) {
      try {
        await response.text();
      } catch (readError) {
        console.error(readError);
      }

      return null;
    }

    // Check the Content-Type to determine how to handle the response
    const contentType = response.headers.get("Content-Type");

    // Direct handling of image binary data
    if (
      contentType &&
      (contentType.includes("image") || contentType.includes("octet-stream"))
    ) {
      try {
        // Get the binary data for direct return
        // The client code will handle creation of the image from this data
        const arrayBuffer = await response.arrayBuffer();

        if (arrayBuffer.byteLength === 0) {
          return null;
        }

        // Convert ArrayBuffer to base64
        const base64 = arrayBufferToBase64(arrayBuffer);
        return base64;
      } catch (binaryError) {
        console.error(binaryError);
        return null;
      }
    }
    // Try to detect if response is binary data despite wrong Content-Type
    else {
      try {
        // First, get the raw response as an ArrayBuffer to check if it's binary
        const clonedResponse = response.clone();
        const arrayBuffer = await clonedResponse.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);

        // Check for PNG/JPEG signatures or other binary patterns
        const isProbablyBinary =
          // PNG signature
          (uint8Array[0] === 0x89 &&
            uint8Array[1] === 0x50 &&
            uint8Array[2] === 0x4e &&
            uint8Array[3] === 0x47) ||
          // JPEG signature
          (uint8Array[0] === 0xff && uint8Array[1] === 0xd8) ||
          // Check if it contains many non-printable characters
          uint8Array
            .slice(0, 100)
            .filter((b) => b < 32 && b !== 9 && b !== 10 && b !== 13).length >
            10;

        if (isProbablyBinary) {
          const base64 = arrayBufferToBase64(arrayBuffer);
          return base64;
        }

        // If it's not binary, get it as text
        const data = await response.text();

        return parseGeminiHttpTextResponse(data);
      } catch (error) {
        console.error(error);

        // Fall back to text
        try {
          const textResponse = await response.text();
          return parseGeminiHttpTextResponse(textResponse);
        } catch (textError) {
          console.error(textError);
          return null;
        }
      }
    }
  } catch (error) {
    console.error(error);

    if (error instanceof TypeError && error.message.includes("NetworkError")) {
      console.error(
        "Network error detected. This might be a CORS or connection issue.",
      );
    }

    return null;
  }
};

// Convert ArrayBuffer to base64
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}
